// src/admin/pages/AdminProducts.js
import React, { useState, useEffect, useContext } from "react";
import AdminSidebar from "../AdminSidebar";
import AdminHeader from "../AdminHeader";
import { FaPlus, FaEdit, FaTrash, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { AdminLanguageContext } from "../context/AdminLanguageContext";
import { getAdminProducts } from "../../services/adminProducts.api";
import { deleteAdminProduct } from "../../services/adminProducts.api";

function AdminProducts() {
    const navigate = useNavigate();
    const { lang } = useContext(AdminLanguageContext);

    const adminUser = JSON.parse(localStorage.getItem("adminUser") || "{}");
const perms = JSON.parse(localStorage.getItem("staffPermissions") || {});
const isAdmin = adminUser.role === "admin";

const canAccessProducts = isAdmin || perms.can_manage_products;


    const TEXT = {
        EN: {
            title: "Products",
            add: "Add Product",
            search: "Search products...",
            image: "Image",
            titleCol: "Title",
            price: "Price",
            stock: "Stock",
            actions: "Actions",
            loading: "Loading products...",
            empty: "No products found.",
            edit: "Edit",
            delete: "Delete",
            prev: "Prev",
            next: "Next",
            deleteTitle: "Delete Product?",
            deleteText: "This action cannot be undone.",
            cancel: "Cancel",
            count: "Total products",
        },
        AR: {
            title: "المنتجات",
            add: "إضافة منتج",
            search: "بحث عن المنتجات...",
            image: "الصورة",
            titleCol: "العنوان",
            price: "السعر",
            stock: "المخزون",
            actions: "الإجراءات",
            loading: "جاري تحميل المنتجات...",
            empty: "لا توجد منتجات.",
            edit: "تعديل",
            delete: "حذف",
            prev: "السابق",
            next: "التالي",
            deleteTitle: "حذف المنتج؟",
            deleteText: "لا يمكن التراجع عن هذا الإجراء.",
            cancel: "إلغاء",
            count: "إجمالي المنتجات",
        },
    }[lang];

    const [isOpen, setIsOpen] = useState(false);
    const [products, setProducts] = useState([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);

    const [sortBy, setSortBy] = useState(null);
    const [sortDirection, setSortDirection] = useState("asc");

    const [page, setPage] = useState(1);
    const perPage = 6;

    const [deleteId, setDeleteId] = useState(null);

    useEffect(() => {
  setLoading(true);
  getAdminProducts()
    .then((data) => {
      const mapped = data.map((p) => ({
        ...p,
        title_en: p.name_en,   // ✅ FIX
        price: p.base_price,   // ✅ FIX
      }));
      setProducts(mapped);
    })
    .catch((err) => console.error("Load products error:", err))
    .finally(() => setLoading(false));
}, []);



    const filtered = products.filter((p) => {
  if (!search) return true;

  const q = search.toLowerCase();

  // basic fields
  const nameEn = (p.name_en || p.title_en || "").toLowerCase();
  const nameAr = (p.name_ar || "").toLowerCase();
  const publicId = (p.public_id || "").toLowerCase();

  // categories (array)
  const categories = (p.categories || [])
    .join(" ")
    .toLowerCase();

  // variants: colors + sizes
 const variantsText = (p.variants || [])
  .map((v) => {
    // ✅ colors
    const colorName =
      v.color_name ||
      v.color ||
      "";

    // ✅ sizes (direct + nested)
    const directSize =
      v.size != null ? String(v.size) : "";

    const nestedSizes = (v.sizes || [])
      .map((s) =>
        s.size != null ? String(s.size) : ""
      )
      .join(" ");

    return `${colorName} ${directSize} ${nestedSizes}`;
  })
  .join(" ")
  .toLowerCase();



  return (
    nameEn.includes(q) ||
    nameAr.includes(q) ||
    publicId.includes(q) ||
    categories.includes(q) ||
    variantsText.includes(q)
  );
});



    const sorted = [...filtered].sort((a, b) => {
        if (!sortBy) return 0;

        let valueA = a[sortBy];
        let valueB = b[sortBy];

        if (typeof valueA === "string") valueA = valueA.toLowerCase();
        if (typeof valueB === "string") valueB = valueB.toLowerCase();

        if (sortDirection === "asc") return valueA > valueB ? 1 : -1;
        return valueA < valueB ? 1 : -1;
    });

    const totalPages = Math.ceil(sorted.length / perPage);
    const paginated = sorted.slice((page - 1) * perPage, page * perPage);

    const changeSort = (field) => {
        if (sortBy === field) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortBy(field);
            setSortDirection("asc");
        }
    };

    const getSortIcon = (field) => {
        if (sortBy !== field) return <FaSort className="inline opacity-40 ml-1" />;
        return sortDirection === "asc" ? (
            <FaSortUp className="inline ml-1" />
        ) : (
            <FaSortDown className="inline ml-1" />
        );
    };

    const confirmDelete = async () => {
  await deleteAdminProduct(deleteId);
  setProducts((prev) => prev.filter((p) => p.id !== deleteId));
  setDeleteId(null);
};


  const groupVariantsByColor = (variants = []) => {
  // ✅ 1) DEDUPE: keep only ONE row per variant.id (joins were duplicating it)
  const byId = new Map();

  (variants || []).forEach((v) => {
    if (!v) return;

    const id = v.id != null ? String(v.id) : null;

    // normalize
const normalized = {
  id,
  color_name: v.color_name || "Unknown",
  color_hex: v.color_hex || "#ccc",
  size: v.size != null ? String(v.size) : "",
  is_available: v.is_available ? 1 : 0,
};

    if (id) {
      // keep first (or keep the bigger qty if you want extra safety)
      if (!byId.has(id)) byId.set(id, normalized);
    } else {
      // fallback if id missing (rare): unique by color+hex+size+qty
      const fallbackKey = `${normalized.color_name}|${normalized.color_hex}|${normalized.size}|${normalized.stock_qty}`;
      if (!byId.has(fallbackKey)) byId.set(fallbackKey, normalized);
    }
  });

  const uniqueVariants = Array.from(byId.values());

  // ✅ 2) GROUP BY COLOR + SUM TOTALS
  const colorsMap = {};

  uniqueVariants.forEach((v) => {
    const cKey = `${v.color_name}|${v.color_hex}`;

    if (!colorsMap[cKey]) {
      colorsMap[cKey] = {
  color_name: v.color_name,
  color_hex: v.color_hex,
  sizes: [],
};
    }

    colorsMap[cKey].sizes.push({
  size: v.size,
  is_available: v.is_available,
});
  });

  // ✅ 3) sort sizes nicely
 return Object.values(colorsMap).map((c) => ({
  ...c,
  sizes: c.sizes.sort((a, b) => {
    const na = Number(a.size);
    const nb = Number(b.size);
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
    return String(a.size).localeCompare(String(b.size));
  }),
}));
};



    return (
        <div className={`flex bg-gray-100 min-h-screen ${isOpen ? "overflow-hidden" : ""}`}>
            <AdminSidebar isOpen={isOpen} setIsOpen={setIsOpen} />

            <div className="flex-1 lg:ml-64 w-full transition-all duration-300">
                <AdminHeader setIsOpen={setIsOpen} />

                <main className="p-4 sm:p-6 animate-fadeSlide">
                 {!canAccessProducts ? (
    <div className="mt-10 bg-red-50 border border-red-200 rounded-xl p-6 text-center">
      <p className="text-red-700 font-semibold text-lg">
        {lang === "EN"
          ? "You are not allowed to access the Products page."
          : "غير مسموح لك بالدخول إلى صفحة المنتجات."}
      </p>
    </div>
  ) : (
    <>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
                        <h1 className="text-2xl font-bold text-cyan-700">
                            {TEXT.title}
                        </h1>

                        <button
                            className="bg-cyan-600 text-white px-4 py-2 rounded-xl flex items-center gap-2 hover:bg-cyan-700"
                            onClick={() => navigate("/admin/products/add")}
                        >
                            <FaPlus /> {TEXT.add}
                        </button>
                    </div>

                    <input
                        type="text"
                        placeholder={TEXT.search}
                        className="w-full p-3 border rounded-xl mb-4 shadow-sm"
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(1);
                        }}
                    />
                     
                     {/* Products count */}
<div className="flex justify-between items-center text-sm text-gray-500 mb-4">
  <span>
    {TEXT.count}:{" "}
    <span className="font-semibold text-gray-700">
      {filtered.length}
    </span>
  </span>

  {search && (
    <span className="italic text-gray-400">
      {lang === "AR" ? "نتائج البحث" : "Search results"}
    </span>
  )}
</div>

                    <div className="hidden md:block bg-white shadow-lg rounded-xl overflow-hidden">
                        <table className="w-full min-w-[700px]">
                            <thead className="bg-gray-200 text-gray-700">
                                <tr>
                                    <th className="p-3 align-middle text-center">{TEXT.image}</th>
                                    <th className="p-3 cursor-pointer align-middle text-center" onClick={() => changeSort("title_en")}>
                                        {TEXT.titleCol} {getSortIcon("title_en")}
                                    </th>
                                    <th className="p-3 cursor-pointer align-middle text-center" onClick={() => changeSort("price")}>
                                        {TEXT.price} {getSortIcon("price")}
                                    </th>
                                    <th className="p-3 cursor-pointer align-middle text-center" onClick={() => changeSort("stock")}>
                                        {TEXT.stock} {getSortIcon("stock")}
                                    </th>
                                    <th className="p-3 text-center align-middle text-center">{TEXT.actions}</th>
                                </tr>
                            </thead>

                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="5" className="p-4 text-center align-middle text-center">
                                            {TEXT.loading}
                                        </td>
                                    </tr>
                                ) : paginated.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="p-4 text-center align-middle text-center">
                                            {TEXT.empty}
                                        </td>
                                    </tr>
                                ) : (
                                    paginated.map((p) => (
                                        <tr key={p.id} className="border-b hover:bg-gray-50">
                                            <td className="p-3 align-middle text-center">
                                                <img
                                                    src={p.image || "/no-image.png"}
                                                    className="w-16 h-16 object-cover rounded"
                                                    alt=""
                                                    onError={(e) => (e.target.src = "/no-image.png")}
                                                />
                                            </td>
                                            <td className="p-3 align-middle text-center">
  <div className="font-semibold">{p.title_en}</div>
<div className="text-xs text-gray-400">ID: {p.public_id}</div>


  {/* Arabic name */}
  {p.name_ar && (
    <div className="text-xs text-gray-500">{p.name_ar}</div>
  )}

  {/* Badges */}
  <div className="flex flex-wrap gap-1 mt-1">
    {p.on_sale ? (
  <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">
    SALE {p.sale_percent}%
  </span>
) : (
  <span className="relative text-xs bg-gray-100 text-gray-400 px-2 py-0.5 rounded">
    SALE
    <span className="absolute inset-0 flex items-center justify-center">
      <span className="w-full h-[1px] bg-red-400 rotate-[-20deg]" />
    </span>
  </span>
)}


    {p.is_bestseller ? (
  <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
    Bestseller
  </span>
) : (
  <span className="relative text-xs bg-gray-100 text-gray-400 px-2 py-0.5 rounded">
    Bestseller
    <span className="absolute inset-0 flex items-center justify-center">
      <span className="w-full h-[1px] bg-purple-400 rotate-[-20deg]" />
    </span>
  </span>
)}


    {p.is_new_in ? (
  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
    New
  </span>
) : (
  <span className="relative text-xs bg-gray-100 text-gray-400 px-2 py-0.5 rounded">
    New
    <span className="absolute inset-0 flex items-center justify-center">
      <span className="w-full h-[1px] bg-green-400 rotate-[-20deg]" />
    </span>
  </span>
)}

  </div>

  {/* Categories */}
  {p.categories?.length > 0 && (
    <div className="text-xs text-gray-400 mt-1">
      Categories: {p.categories.join(", ")}
    </div>
  )}

  {/* Tags */}
  {p.tags?.length > 0 && (
    <div className="text-xs text-gray-400">
      Tags: #{p.tags.join(" #")}
    </div>
  )}
</td>

                                           <td className="p-3 align-middle text-center">
  {!p.on_sale ? (
    <div className="font-semibold">${Number(p.price).toFixed(2)}</div>
  ) : (
    <div className="space-y-0.5">
      {/* base price */}
      <div className="text-sm text-gray-500 line-through">
        ${Number(p.price).toFixed(2)}
      </div>

      {/* discount percent */}
      <div className="text-xs font-bold text-red-600">
        -{p.sale_percent}%
      </div>

      {/* new price */}
      <div className="font-semibold text-green-600">
        $
        {(
          Number(p.price) -
          (Number(p.price) * Number(p.sale_percent || 0)) / 100
        ).toFixed(2)}
      </div>
    </div>
  )}
</td>


                                           <td className="p-3 align-middle text-center">
  

  {/* PER COLOR */}
  <div className="space-y-2">
    {groupVariantsByColor(p.variants).map((c, i) => (
      <div
        key={i}
        className="border rounded-lg p-2 text-left text-xs bg-gray-50"
      >
        {/* Color header */}
        <div className="flex items-center gap-2 mb-1">
          <span
            className="w-3 h-3 rounded-full border"
            style={{ backgroundColor: c.color_hex }}
          />
          <span className="font-semibold">
            {c.color_name}
          </span>
        </div>

        {/* Sizes */}
        <div className="flex flex-wrap gap-2">
         {c.sizes.map((s, j) => (
  <span
    key={j}
    className={`px-2 py-0.5 rounded-full text-xs font-semibold
      ${s.is_available
        ? "bg-green-100 text-green-700"
        : "bg-red-100 text-red-700"}
    `}
  >
    {s.size}: {s.is_available ? "Available" : "Not Available"}
  </span>
))}

        </div>
      </div>
    ))}
  </div>
</td>



                                            <td className="p-3 flex justify-center gap-3 align-middle text-center">
                                                <button
                                                    className="text-blue-600"
                                                    onClick={() => navigate(`/admin/products/edit/${p.id}`)}
                                                >
                                                    <FaEdit />
                                                </button>
                                                <button
                                                    className="text-red-600"
                                                    onClick={() => setDeleteId(p.id)}
                                                >
                                                    <FaTrash />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* MOBILE CARDS */}
<div className="md:hidden space-y-4">
  {loading ? (
    <div className="text-center p-4">{TEXT.loading}</div>
  ) : paginated.length === 0 ? (
    <div className="text-center p-4">{TEXT.empty}</div>
  ) : (
    paginated.map((p) => (
      <div
        key={p.id}
        className="bg-white rounded-xl shadow p-4 space-y-3"
      >
        {/* Header */}
        <div className="flex items-center gap-3">
          <img
            src={p.image || "/no-image.png"}
            className="w-16 h-16 rounded object-cover"
            alt=""
          />
          <div>
            <div className="font-bold">{p.title_en}</div>
            <div className="text-xs text-gray-400">ID: {p.public_id}</div>
            {p.name_ar && (
              <div className="text-xs text-gray-500">{p.name_ar}</div>
            )}
          </div>
        </div>

        {/* Price */}
        <div className="flex justify-between text-sm">
  <span>{TEXT.price}</span>

  {!p.on_sale ? (
    <span className="font-bold">${Number(p.price).toFixed(2)}</span>
  ) : (
    <div className="text-right">
      <div className="text-xs text-gray-500 line-through">
        ${Number(p.price).toFixed(2)}
      </div>
      <div className="text-xs font-bold text-red-600">
        -{p.sale_percent}%
      </div>
      <div className="font-bold text-green-600">
        $
        {(
          Number(p.price) -
          (Number(p.price) * Number(p.sale_percent || 0)) / 100
        ).toFixed(2)}
      </div>
    </div>
  )}
</div>


        {/* Stock */}
        <div>

  {/* COLORS */}
  <div className="space-y-2">
    {groupVariantsByColor(p.variants).map((c, i) => (
      <div
        key={i}
        className="border rounded-lg p-2 bg-gray-50"
      >
        <div className="flex items-center gap-2 text-sm font-semibold mb-1">
          <span
            className="w-3 h-3 rounded-full border"
            style={{ backgroundColor: c.color_hex }}
          />
          {c.color_name}
      
        </div>

        <div className="flex flex-wrap gap-2 text-xs">
        {c.sizes.map((s, j) => (
  <span
    key={j}
    className={`px-2 py-0.5 rounded-full text-xs font-semibold
      ${s.is_available
        ? "bg-green-100 text-green-700"
        : "bg-red-100 text-red-700"}
    `}
  >
    {s.size}: {s.is_available ? "Available" : "Not Available"}
  </span>
))}

        </div>
      </div>
    ))}
  </div>
</div>


        {/* Bottom row: badges on the left + actions on the right */}
<div className="flex items-start justify-between gap-3 pt-2 border-t">
  {/* Badges (same style as desktop) */}
  <div className="flex flex-wrap gap-1">
    {p.on_sale ? (
      <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">
        SALE {p.sale_percent}%
      </span>
    ) : (
      <span className="relative text-xs bg-gray-100 text-gray-400 px-2 py-0.5 rounded">
        SALE
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="w-full h-[1px] bg-red-400 rotate-[-20deg]" />
        </span>
      </span>
    )}

    {p.is_bestseller ? (
      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
        Bestseller
      </span>
    ) : (
      <span className="relative text-xs bg-gray-100 text-gray-400 px-2 py-0.5 rounded">
        Bestseller
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="w-full h-[1px] bg-purple-400 rotate-[-20deg]" />
        </span>
      </span>
    )}

    {p.is_new_in ? (
      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
        New
      </span>
    ) : (
      <span className="relative text-xs bg-gray-100 text-gray-400 px-2 py-0.5 rounded">
        New
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="w-full h-[1px] bg-green-400 rotate-[-20deg]" />
        </span>
      </span>
    )}
  </div>

  {/* Actions */}
  <div className="flex gap-4">
    <button
      className="text-blue-600"
      onClick={() => navigate(`/admin/products/edit/${p.id}`)}
    >
      <FaEdit />
    </button>
    <button
      className="text-red-600"
      onClick={() => setDeleteId(p.id)}
    >
      <FaTrash />
    </button>
  </div>
</div>

      </div>
    ))
  )}
</div>


                    {!loading && filtered.length > 0 && (
  <div className="flex justify-center mt-8">
    <div className="flex items-center gap-3 bg-white shadow-md rounded-xl px-4 py-2">

      {/* Prev */}
      <button
        disabled={page === 1}
        onClick={() => setPage(page - 1)}
        className={`px-3 py-1 rounded-lg border text-sm transition
          ${
            page === 1
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-white hover:bg-cyan-50 text-cyan-700 border-cyan-300"
          }
        `}
      >
        {TEXT.prev}
      </button>

      {/* Page indicator */}
      <span className="text-sm font-semibold text-gray-600 whitespace-nowrap">
        {lang === "AR"
          ? `صفحة ${page} من ${totalPages}`
          : `Page ${page} of ${totalPages}`}
      </span>

      {/* Next */}
      <button
        disabled={page === totalPages}
        onClick={() => setPage(page + 1)}
        className={`px-3 py-1 rounded-lg border text-sm transition
          ${
            page === totalPages
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-white hover:bg-cyan-50 text-cyan-700 border-cyan-300"
          }
        `}
      >
        {TEXT.next}
      </button>

    </div>
  </div>
)}


                    {deleteId !== null && (
                        <div className="fixed inset-0 bg-black/40 flex items-center justify-center">
                            <div className="bg-white p-6 rounded-xl shadow-xl w-80">
                                <h2 className="text-lg font-bold text-red-600 mb-2">
                                    {TEXT.deleteTitle}
                                </h2>
                                <p className="mb-5">{TEXT.deleteText}</p>

                                <div className="flex justify-end gap-3">
                                    <button
                                        className="px-4 py-2 bg-gray-300 rounded-lg"
                                        onClick={() => setDeleteId(null)}
                                    >
                                        {TEXT.cancel}
                                    </button>
                                    <button
                                        className="px-4 py-2 bg-red-500 text-white rounded-lg"
                                        onClick={confirmDelete}
                                    >
                                        {TEXT.delete}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                 </>
  )}
</main>
            </div>
        </div>
    );
}

export default AdminProducts;
